import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import { nanoid } from 'nanoid'
import { uniquePreserveOrder } from '@/util/array-utils'
import { DeleteSelectionMode } from '@/user_files/delete/schemas'
import type { FileSelectionPrecondition, FolderPathPrecondition, FolderSelectionPrecondition } from '@/user_files/schemas/preconditions'

export enum UserFilesBatchedOperationKind {
  MoveSelection = 'move_selection',
  DeleteSelection = 'delete_selection',
}

export enum UserFilesBatchedOperationStatus {
  Queued = 'queued',
  Running = 'running',
  Succeeded = 'succeeded',
  Failed = 'failed',
}

export type UserFilesBatchedOperation = {
  id: string
  kind: UserFilesBatchedOperationKind
  status: UserFilesBatchedOperationStatus
  createdAt: number
  startedAt?: number
  finishedAt?: number

  /**
   * The folder whose listing is refreshed and invalidated once the operation finishes — in
   * practice the folder the user launched it from. It never reaches the server, and a delete has
   * no source-and-destination pair, so this is a cache target rather than the items' origin: in
   * search results the selection can span folders and none of them need be this one.
   */
  refreshFolderId: string
  destinationFolderId?: string

  fileIds: string[]
  folderIds: string[]
  filePreconditions?: FileSelectionPrecondition[]
  folderPreconditions?: FolderSelectionPrecondition[]
  destinationFolderPrecondition?: FolderPathPrecondition
  /** Delete operations only. */
  deleteMode?: DeleteSelectionMode
  /**
   * How many requests this operation carries, and how many have finished — the progress fraction.
   * Pruning happens before the enqueue, so this counts the top-most items sent rather than
   * everything the user ticked.
   */
  totalItems: number
  processedItems: number
  /**
   * How many rows the user ticked, which is what the completion toast reports. It matches
   * `totalItems` unless the selection was pruned, i.e. it spanned a folder and something inside it
   * (FE-594 / BB-04).
   */
  selectedItems: number

  errorMessage?: string
}

export type EnqueueDeleteSelectionPayload = {
  /**
   * The folder whose listing is refreshed and invalidated once the operation finishes — in
   * practice the folder the user launched it from. It never reaches the server, and a delete has
   * no source-and-destination pair, so this is a cache target rather than the items' origin: in
   * search results the selection can span folders and none of them need be this one.
   */
  refreshFolderId: string
  fileIds?: string[]
  folderIds?: string[]
  filePreconditions?: FileSelectionPrecondition[]
  folderPreconditions?: FolderSelectionPrecondition[]
  /** Defaults to deleting everything the selection covers. */
  mode?: DeleteSelectionMode
  /** How many rows the user ticked. Defaults to the number of ids being sent. */
  selectedItems?: number
}

export type EnqueueMoveSelectionPayload = {
  /**
   * The folder whose listing is refreshed and invalidated once the operation finishes — in
   * practice the folder the user launched it from. It never reaches the server, and a delete has
   * no source-and-destination pair, so this is a cache target rather than the items' origin: in
   * search results the selection can span folders and none of them need be this one.
   */
  refreshFolderId: string
  destinationFolderId: string
  fileIds?: string[]
  folderIds?: string[]
  filePreconditions?: FileSelectionPrecondition[]
  folderPreconditions?: FolderSelectionPrecondition[]
  destinationFolderPrecondition?: FolderPathPrecondition
  /** How many rows the user ticked. Defaults to the number of ids being sent. */
  selectedItems?: number
}

export type UserFilesBatchingState = {
  activeOperation: UserFilesBatchedOperation | null
}

const initialState: UserFilesBatchingState = {
  activeOperation: null,
}

export const userFilesBatchingSlice = createSlice({
  name: 'userFilesBatchingState',
  initialState,
  reducers: {
    enqueueDeleteSelection: (state, action: PayloadAction<EnqueueDeleteSelectionPayload>) => {
      const isActive =
        state.activeOperation?.status === UserFilesBatchedOperationStatus.Queued || state.activeOperation?.status === UserFilesBatchedOperationStatus.Running
      if (isActive) return

      const fileIds = uniquePreserveOrder(action.payload.fileIds ?? [])
      const folderIds = uniquePreserveOrder(action.payload.folderIds ?? [])
      if (fileIds.length === 0 && folderIds.length === 0) return

      state.activeOperation = {
        id: nanoid(),
        kind: UserFilesBatchedOperationKind.DeleteSelection,
        status: UserFilesBatchedOperationStatus.Queued,
        createdAt: Date.now(),
        refreshFolderId: action.payload.refreshFolderId,
        fileIds,
        folderIds,
        filePreconditions: action.payload.filePreconditions,
        folderPreconditions: action.payload.folderPreconditions,
        deleteMode: action.payload.mode,
        totalItems: fileIds.length + folderIds.length,
        processedItems: 0,
        selectedItems: action.payload.selectedItems ?? fileIds.length + folderIds.length,
      }
    },

    enqueueMoveSelection: (state, action: PayloadAction<EnqueueMoveSelectionPayload>) => {
      const isActive =
        state.activeOperation?.status === UserFilesBatchedOperationStatus.Queued || state.activeOperation?.status === UserFilesBatchedOperationStatus.Running
      if (isActive) return

      const fileIds = uniquePreserveOrder(action.payload.fileIds ?? [])
      const folderIds = uniquePreserveOrder(action.payload.folderIds ?? [])
      if (fileIds.length === 0 && folderIds.length === 0) return

      state.activeOperation = {
        id: nanoid(),
        kind: UserFilesBatchedOperationKind.MoveSelection,
        status: UserFilesBatchedOperationStatus.Queued,
        createdAt: Date.now(),
        refreshFolderId: action.payload.refreshFolderId,
        destinationFolderId: action.payload.destinationFolderId,
        fileIds,
        folderIds,
        filePreconditions: action.payload.filePreconditions,
        folderPreconditions: action.payload.folderPreconditions,
        destinationFolderPrecondition: action.payload.destinationFolderPrecondition,
        totalItems: fileIds.length + folderIds.length,
        processedItems: 0,
        selectedItems: action.payload.selectedItems ?? fileIds.length + folderIds.length,
      }
    },

    operationStarted: (state, action: PayloadAction<{ operationId: string }>) => {
      const op = state.activeOperation
      if (!op || op.id !== action.payload.operationId) return
      op.status = UserFilesBatchedOperationStatus.Running
      op.startedAt = Date.now()
      op.processedItems = 0
      delete op.errorMessage
    },

    operationProgressUpdated: (state, action: PayloadAction<{ operationId: string; processedItems: number }>) => {
      const op = state.activeOperation
      if (!op || op.id !== action.payload.operationId) return
      op.processedItems = action.payload.processedItems
    },

    operationSucceeded: (state, action: PayloadAction<{ operationId: string }>) => {
      const op = state.activeOperation
      if (!op || op.id !== action.payload.operationId) return
      op.status = UserFilesBatchedOperationStatus.Succeeded
      op.finishedAt = Date.now()
      op.processedItems = op.totalItems
    },

    operationFailed: (state, action: PayloadAction<{ operationId: string; errorMessage: string }>) => {
      const op = state.activeOperation
      if (!op || op.id !== action.payload.operationId) return
      op.status = UserFilesBatchedOperationStatus.Failed
      op.finishedAt = Date.now()
      op.errorMessage = action.payload.errorMessage
    },

    clearActiveOperation: (state) => {
      state.activeOperation = null
    },
  },
})

export const UserFilesBatchingActions = userFilesBatchingSlice.actions
export default userFilesBatchingSlice.reducer
